<script setup lang="ts">
/**
 * Writing surface for the stylus-keyboard mode.
 *
 * The recognition belongs to the device (iPad Scribble, Samsung S Pen):
 * both trigger when the stylus writes on a real editable field, and neither
 * can be invoked from JavaScript. So this dialog is nothing but a large
 * blank field, focused as soon as it opens — the system handwriting input
 * only engages on a focused field.
 *
 * No inputmode="none" here, tempting as it is to keep the keyboard away:
 * Android routes stylus handwriting through the input method itself, so
 * suppressing the keyboard ends the handwriting session after every
 * insertion — the user can then only write one letter at a time. The
 * keyboard is kept at bay by geometry instead: the sheet sits at the top
 * of the screen and shrinks to whatever the keyboard leaves visible.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_MODAL } from "@/components/canvas/layouts/constants";

const { t } = useTranslation();

const visible = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  (e: "validate", text: string): void;
  (e: "cancel"): void;
}>();

const text = ref("");
const sheetRef = useTemplateRef<HTMLTextAreaElement>("sheetRef");

/** Delay of the second focus attempt, after the dialog's focus trap. */
const FOCUS_RETRY_MS = 150;

/** Room the dialog needs around the sheet: top offset, actions, margins. */
const CHROME_HEIGHT_PX = 150;
/** Never shrink the writing area below this, keyboard or not. */
const MIN_SHEET_HEIGHT_PX = 180;
/** Nor grow it past this on a large screen. */
const MAX_SHEET_HEIGHT_PX = 460;

/**
 * Each placement starts from an empty, focused sheet. Focused twice on
 * purpose: the dialog grabs the focus back right after it is shown.
 */
watch(visible, (isOpen) => {
  if (!isOpen) return;
  text.value = "";
  trackVisibleHeight();
  nextTick(() => sheetRef.value?.focus());
  setTimeout(() => sheetRef.value?.focus(), FOCUS_RETRY_MS);
});

/**
 * Height of the sheet, kept inside what the on-screen keyboard leaves
 * visible (visualViewport shrinks when the keyboard opens, unlike vh).
 */
const visibleHeight = ref(
  typeof window !== "undefined" ? window.innerHeight : 800,
);

const sheetHeight = computed(() => {
  const available = visibleHeight.value - CHROME_HEIGHT_PX;
  const clamped = Math.min(
    Math.max(available, MIN_SHEET_HEIGHT_PX),
    MAX_SHEET_HEIGHT_PX,
  );
  return `${clamped}px`;
});

function trackVisibleHeight() {
  visibleHeight.value = window.visualViewport?.height ?? window.innerHeight;
}

window.visualViewport?.addEventListener("resize", trackVisibleHeight);
onBeforeUnmount(() => {
  window.visualViewport?.removeEventListener("resize", trackVisibleHeight);
});

function validate() {
  emit("validate", text.value);
  visible.value = false;
}

function cancel() {
  emit("cancel");
  visible.value = false;
}
</script>

<template>
  <el-dialog
    v-model="visible"
    class="stylus-dialog"
    width="min(760px, 96vw)"
    top="6vh"
    :show-close="false"
    :close-on-click-modal="false"
    append-to-body
    :z-index="Z_INDEX_MODAL"
    @close="cancel"
  >
    <div class="stylus-surface">
      <!-- Reminder that the tool is not released to inspectors yet -->
      <span class="stylus-badge">{{
        t("canvas:tools.stylusKeyboard.superadminBadge")
      }}</span>

      <textarea
        ref="sheetRef"
        v-model="text"
        class="stylus-sheet"
        :style="{ height: sheetHeight }"
        :placeholder="t('canvas:tools.stylusKeyboard.placeholder')"
        spellcheck="false"
      />

      <div class="stylus-actions">
        <button
          class="stylus-action"
          type="button"
          :title="t('common:cancel')"
          @click="cancel"
        >
          <PhosphorIcon name="x" :size="22" />
        </button>
        <button
          class="stylus-action stylus-action-confirm"
          type="button"
          :title="t('canvas:tools.stylusKeyboard.validate')"
          @click="validate"
        >
          <PhosphorIcon name="check" weight="bold" :size="22" />
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss">
// Unscoped on purpose: the dialog shell is rendered by Element Plus.
// The dialog IS the writing field — no frame, no header, no padding.
.stylus-dialog {
  background: transparent;
  box-shadow: none;
  padding: 0;

  .el-dialog__header,
  .el-dialog__footer {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
  }
}
</style>

<style lang="scss" scoped>
.stylus-surface {
  position: relative;
}

// A sheet of paper: white whatever the theme, because it is the surface
// the user writes on with the stylus.
.stylus-sheet {
  display: block;
  width: 100%;
  // Height is set inline: it follows the space left by the keyboard.
  // Bottom room for the floating actions, top room for the badge.
  padding: 3rem 1.25rem 5rem;
  border: none;
  border-radius: 10px;
  background-color: #ffffff;
  color: #1a1a1a;
  font-size: 1.5rem;
  line-height: 1.6;
  resize: none;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #9aa0a6;
  }
}

.stylus-badge {
  position: absolute;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 10px;
  border-radius: 999px;
  background-color: var(--bs-warning);
  color: #1a1a1a;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  // Never in the way of the stylus.
  pointer-events: none;
}

.stylus-actions {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
}

.stylus-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  background-color: #ffffff;
  color: #1a1a1a;
}

.stylus-action-confirm {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary);
  color: #ffffff;
}
</style>
